/**
 * Thrown when the server rejects a request because the caller isn't signed in or isn't
 * whitelisted — distinguished from other failures so the app can show the auth gate instead
 * of a generic error toast (see `App`'s `renderAuthGate`).
 */
export class ApiAuthError extends Error {
  constructor(
    message: string,
    readonly status: 401 | 403,
    readonly email?: string,
  ) {
    super(message);
    this.name = "ApiAuthError";
  }
}

/**
 * Thin wrapper around the stateful API (`server/`), same-origin behind nginx (see
 * `deploy/nginx/`) so no base URL configuration is needed — `/api` resolves relative to
 * wherever this build is served from, in dev (Vite proxies it, see `vite.config.ts`) and in
 * production alike.
 */
export class ApiClient {
  constructor(private readonly getToken: () => Promise<string>) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`/api${path}`, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${token}` },
    });
    if (response.status === 401 || response.status === 403) {
      const body = await response.json().catch(() => ({}) as { error?: string; email?: string });
      throw new ApiAuthError(body.error ?? `HTTP ${response.status}`, response.status, body.email);
    }
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`API ${response.status} on ${path}${body ? `: ${body.slice(0, 200)}` : ""}`);
    }
    return (await response.json()) as T;
  }

  /** The auth-gate probe: resolves to the caller's verified email once signed in and
   *  whitelisted, or throws {@link ApiAuthError} otherwise. */
  async me(): Promise<{ email: string | null }> {
    return this.request("/me");
  }
}
